import React from 'react';
import {
  Scale, TrendingUp, DollarSign, BarChart3,
  CheckCircle2, AlertTriangle, ArrowRight, Trophy
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { formatCurrency } from '../../lib/utils';
import { recordProjectionAccuracy } from '../../lib/api';
import { toast } from 'sonner';

const calculateAccuracy = (projected, actual) => {
  if (actual === 0) return { diff: 0, status: 'neutral', label: 'N/A' };
  const diff = ((projected - actual) / Math.abs(actual)) * 100;
  const absDiff = Math.abs(diff);

  if (absDiff <= 10) {
    return { diff, status: 'accurate', label: 'Accurate' };
  } else if (diff > 0) {
    return { diff, status: 'over', label: `+${diff.toFixed(1)}% over` };
  } else {
    return { diff, status: 'under', label: `${diff.toFixed(1)}% under` };
  }
};

const getAccuracyBadgeStyle = (status) => {
  switch (status) {
    case 'accurate': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'over': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'under': return 'bg-blue-100 text-blue-700 border-blue-200';
    default: return 'bg-slate-100 text-slate-600 border-slate-200';
  }
};

export const ProjectionAccuracyTab = ({ projectedPairs, setViewMode, currency }) => {
  return (
    <div className="space-y-6">
      <Card className="bg-emerald-50 border-emerald-200">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-start gap-2">
            <Scale className="h-4 w-4 text-emerald-600 mt-0.5" />
            <div className="text-sm text-emerald-800">
              <p className="font-medium mb-1">Compare Projections Against Actual Results</p>
              <p className="text-emerald-700">
                See how accurate your What-If projections were compared to actual scenario results.
                Projections within ±10% are considered accurate.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {projectedPairs.length > 0 ? (
        <div className="space-y-6">
          {projectedPairs.map(({ projected, original }) => {
            const projectedRoi = projected.results?.roi_percent || 0;
            const actualRoi = original.results?.roi_percent || original.results?.total_roi_percent || 0;
            const roiAccuracy = calculateAccuracy(projectedRoi, actualRoi);

            const projectedFinancial = projected.results?.financial_impact || 0;
            const actualFinancial = original.results?.financial_impact || original.results?.total_financial_impact || 0;
            const financialAccuracy = calculateAccuracy(projectedFinancial, actualFinancial);

            const projectedKpi = projected.results?.kpi_delta_percent || 0;
            const actualKpi = original.results?.kpi_delta_percent || original.results?.total_kpi_delta_percent || 0;
            const kpiAccuracy = calculateAccuracy(projectedKpi, actualKpi);

            const projectedLei = projected.configuration?.lei_improvement || 0;
            const originalLei = projected.configuration?.original_lei_improvement || original.configuration?.lei_improvement || 0;

            const avgAccuracy = (Math.abs(roiAccuracy.diff) + Math.abs(financialAccuracy.diff) + Math.abs(kpiAccuracy.diff)) / 3;
            const overallStatus = avgAccuracy <= 10 ? 'accurate' : avgAccuracy <= 20 ? 'moderate' : 'poor';

            return (
              <Card key={projected.id} className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-slate-50 pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2 flex-wrap">
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Projected</Badge>
                        {projected.name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Compared against: <span className="font-medium text-slate-700">{original.name}</span>
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                        overallStatus === 'accurate' ? 'bg-emerald-100 text-emerald-700' :
                        overallStatus === 'moderate' ? 'bg-amber-100 text-amber-700' :
                        'bg-rose-100 text-rose-700'
                      }`}>
                        {overallStatus === 'accurate' ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                        {overallStatus === 'accurate' ? 'Highly Accurate' :
                         overallStatus === 'moderate' ? 'Moderately Accurate' : 'Needs Calibration'}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Avg. deviation: {avgAccuracy.toFixed(1)}%</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {/* LEI Comparison Header */}
                  <div className="flex items-center justify-center gap-4 mb-6 p-3 bg-slate-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-xs text-slate-500">Original LEI</p>
                      <p className="text-xl font-bold text-slate-700">{originalLei}%</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-400" />
                    <div className="text-center">
                      <p className="text-xs text-amber-600">Projected LEI</p>
                      <p className="text-xl font-bold text-amber-700">{projectedLei}%</p>
                    </div>
                  </div>

                  {/* Metrics Comparison Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* ROI */}
                    <MetricComparisonCard
                      icon={<TrendingUp className="h-4 w-4 text-teal-600" />}
                      label="ROI"
                      projected={`${(projectedRoi / 100).toFixed(2)}x`}
                      actual={`${(actualRoi / 100).toFixed(2)}x`}
                      accuracy={roiAccuracy}
                      projectedRaw={projectedRoi}
                      actualRaw={actualRoi}
                    />
                    {/* Financial Impact */}
                    <MetricComparisonCard
                      icon={<DollarSign className="h-4 w-4 text-emerald-600" />}
                      label="Financial Impact"
                      projected={formatCurrency(Math.round(projectedFinancial, currency))}
                      actual={formatCurrency(Math.round(actualFinancial, currency))}
                      accuracy={financialAccuracy}
                      projectedRaw={projectedFinancial}
                      actualRaw={actualFinancial}
                    />
                    {/* KPI Uplift */}
                    <MetricComparisonCard
                      icon={<BarChart3 className="h-4 w-4 text-purple-600" />}
                      label="KPI Uplift"
                      projected={`${projectedKpi.toFixed(2)}%`}
                      actual={`${actualKpi.toFixed(2)}%`}
                      accuracy={kpiAccuracy}
                      projectedRaw={projectedKpi}
                      actualRaw={actualKpi}
                    />
                  </div>

                  {/* Legend */}
                  <div className="mt-4 pt-4 border-t flex items-center justify-between">
                    <div className="flex items-center gap-6 text-xs text-slate-500">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-amber-200 rounded" />
                        <span>Projected</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 bg-slate-300 rounded" />
                        <span>Actual</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                        <span>Within ±10% = Accurate</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-yellow-700 border-yellow-300 hover:bg-yellow-50"
                      onClick={async () => {
                        try {
                          await recordProjectionAccuracy(projected.id, original.id);
                          toast.success('Recorded to Accuracy Leaderboard!', {
                            description: 'Check the leaderboard to see your ranking.'
                          });
                        } catch (error) {
                          toast.error('Failed to record accuracy');
                        }
                      }}
                      data-testid={`record-accuracy-${projected.id}`}
                    >
                      <Trophy className="h-4 w-4 mr-1.5" />
                      Record to Leaderboard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-16">
            <div className="text-center">
              <Scale className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No Projected Scenarios Yet</h3>
              <p className="text-slate-500 mt-2 max-w-md mx-auto">
                Use the What-If mode to create projections, save them, and compare against actual results here.
              </p>
              <Button
                onClick={() => setViewMode('compare')}
                className="mt-6 bg-navy-700 hover:bg-navy-800"
              >
                Go to Compare Scenarios
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const MetricComparisonCard = ({ icon, label, projected, actual, accuracy, projectedRaw, actualRaw }) => {
  const maxVal = Math.max(Math.abs(projectedRaw), Math.abs(actualRaw), 0.01);
  return (
    <div className="p-4 border rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-slate-700">{label}</span>
        </div>
        <Badge variant="outline" className={`text-[10px] ${getAccuracyBadgeStyle(accuracy.status)}`}>
          {accuracy.label}
        </Badge>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-xs text-slate-500">Projected:</span>
          <span className="font-semibold text-amber-600">{projected}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-slate-500">Actual:</span>
          <span className="font-semibold text-slate-700">{actual}</span>
        </div>
        <div className="mt-2 space-y-1">
          <div className="h-2 bg-amber-200 rounded-full" style={{ width: `${Math.min(100, (Math.abs(projectedRaw) / maxVal) * 100)}%` }} />
          <div className="h-2 bg-slate-300 rounded-full" style={{ width: `${Math.min(100, (Math.abs(actualRaw) / maxVal) * 100)}%` }} />
        </div>
      </div>
    </div>
  );
};
